import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, LogOut, UserX } from 'lucide-react';
import { FeedbackHost } from '../components/layout/FeedbackHost';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { logoutUser } from '../features/auth/authThunks';
import { operatorGestured, operatorTouched } from '../features/operator/operatorDeviceSlice';
import { OPERATOR_ERROR } from '../features/operator/operatorErrors';
import { fetchOperatorContext } from '../features/operator/operatorThunks';
import {
  selectCurrentOperatorWarehouse,
  selectNeedsAudioUnlock,
  selectOperatorPrefs,
} from '../features/operator/operatorUtils';
import { operatorClockStarted, operatorClockStopped } from '../features/operatorTasks/operatorTasksSlice';
import { FeedbackBand } from '../workspaces/operator/components/FeedbackBand';
import { OperatorLiveRegion } from '../workspaces/operator/components/OperatorLiveRegion';
import { OperatorMenu } from '../workspaces/operator/components/OperatorMenu';
import { OperatorTopBar } from '../workspaces/operator/components/OperatorTopBar';
import { operatorScreenOf } from '../workspaces/operator/operatorNavigation';

const ContextError = ({ error, onRetry, onLogout }) => {
  const notOperator = error.kind === OPERATOR_ERROR.FORBIDDEN;
  return (
    <div data-testid="operator-context-error" className="pt-6">
      <ErrorState
        icon={notOperator ? UserX : undefined}
        title={notOperator ? 'Tu usuario no tiene perfil de operario' : 'No pudimos cargar tu espacio de trabajo'}
        description={
          notOperator
            ? 'Esta sección es para operarios de bodega. Si deberías tener acceso, pídeselo a tu supervisor.'
            : error.message
        }
        onRetry={notOperator ? undefined : onRetry}
      >
        <Button variant="ghost" icon={LogOut} onClick={onLogout}>
          Cerrar sesión
        </Button>
      </ErrorState>
    </div>
  );
};

/**
 * The warehouse operator's shell, built for a phone in one hand: a bar on
 * top, the band that says how the last action went, the screen, and each
 * screen's own action bar at the bottom edge. No sidebar and no socket: the
 * queues are asked again when a screen opens. While it is mounted, a local
 * clock moves the times on screen and, while the operator is still, asks the
 * list on screen again (`operatorRefresh`): each touch is noted, so it never
 * does right after one. A live region says each outcome to screen readers.
 *
 * Everything hangs from `GET operators/me/`, asked once per session.
 */
export const OperatorLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loaded, error } = useSelector((state) => state.operator);
  const warehouse = useSelector(selectCurrentOperatorWarehouse);
  const prefs = useSelector(selectOperatorPrefs);
  const needsAudioUnlock = useSelector(selectNeedsAudioUnlock);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!loaded) dispatch(fetchOperatorContext());
  }, [dispatch, loaded]);

  useEffect(() => {
    dispatch(operatorClockStarted());
    return () => {
      dispatch(operatorClockStopped());
    };
  }, [dispatch]);

  const screen = operatorScreenOf(location.pathname, location.state);
  const logout = () => dispatch(logoutUser(navigate));

  let content = <Outlet />;
  if (!loaded && error) {
    content = <ContextError error={error} onRetry={() => dispatch(fetchOperatorContext())} onLogout={logout} />;
  } else if (!loaded) {
    content = (
      <div data-testid="operator-loading" className="flex justify-center py-16">
        <Loader2 size={32} className="animate-spin text-ninja-green" />
      </div>
    );
  }

  return (
    // iOS plays no sound until a tap allows it; the first tap anywhere does.
    <div
      data-testid="operator-layout"
      className="flex min-h-dvh flex-col bg-ninja-gray dark:bg-[#0f0f0f]"
      onClickCapture={needsAudioUnlock ? () => dispatch(operatorGestured()) : undefined}
      onPointerDownCapture={() => dispatch(operatorTouched(Date.now()))}
    >
      <header className="sticky top-0 z-40 border-b border-border bg-white pt-[env(safe-area-inset-top)] dark:bg-[#1a1a1a]">
        <div className="mx-auto max-w-2xl">
          <OperatorTopBar
            title={screen.title}
            backTo={loaded ? screen.backTo : null}
            warehouseName={warehouse?.name}
            muted={!prefs.sound}
            onMenu={() => setMenuOpen(true)}
          />
        </div>
        <FeedbackBand />
      </header>
      <OperatorLiveRegion />

      {/* The bottom room is the action bar's, fixed over the page: the end of every screen scrolls clear of it. */}
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-[calc(6.5rem_+_env(safe-area-inset-bottom))] pt-4">
        {content}
      </main>

      <OperatorMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
      <FeedbackHost placement="aboveActionBar" />
    </div>
  );
};
